import { css } from '@emotion/react';
import { BirdiTheme, breakpoints, fonts } from '@birdi/theme';

// Background images
export const backgroundImage = css`
  background-image: url('https://birdi-public.s3-ap-southeast-2.amazonaws.com/signup-flow/background.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

export const leftSideImage = css`
  background-image: url('https://birdi-public.s3-ap-southeast-2.amazonaws.com/signup-flow/timeline-volume.png');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
`;

// Gradient background (from image 3: linear gradient and #BFDBFE)
export const gradientBackground = css`
  background: linear-gradient(135deg, #bfdbfe 0%, #f7f6f5 100%);
`;

// Main container
export const pageContainer = css`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  overflow: hidden;
  font-family: ${fonts.body};
`;

// Left side (desktop only)
export const leftSide = (theme: BirdiTheme) => css`
  display: none;
  flex: 1;
  position: relative;
  ${gradientBackground};
  min-width: 0;
  overflow: hidden;

  @media (min-width: ${breakpoints.s}) {
    display: flex;
    align-items: center;
    justify-content: center;
    /* No left padding so images can extend to the edge */
    padding: 2rem 2rem 2rem 0;
  }
`;

// Right side (form container)
export const rightSide = (theme: BirdiTheme) => css`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 0.5rem;
  background-color: ${theme.mono10};
  position: relative;
  z-index: 2;
  overflow-y: auto;
  min-width: 0;

  @media (min-width: ${breakpoints.s}) {
    /* Reduced horizontal padding - scrollableContent handles inner padding */
    padding: 2rem 1.25rem;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.mono10};
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.mono30};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.mono40};
  }
`;

// Content card
export const contentCard = (theme: BirdiTheme) => css`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
  overflow: hidden;
`;

// Logo container - for consistent logo styling across all auth pages
export const logoContainer = css`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  flex-shrink: 0;

  svg {
    height: 4.5rem;
    width: auto;
  }

  // /* Larger logo and more top space on large screens for better alignment */
  // @media (min-width: ${breakpoints.s}) {
  //   margin-top: 1rem;
  //   margin-bottom: 1.5rem;

  //   svg {
  //     height: 5.5rem;
  //   }
  // }

  // /* Even larger on very large screens */
  // @media (min-width: ${breakpoints.xl}) {
  //   margin-top: 2rem;

  //   svg {
  //     height: 6rem;
  //   }
  // }
`;

// Scrollable content area
export const scrollableContent = css`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  /* Symmetric padding - keeps scrollbar from covering content */
  padding-left: 0.75rem;
  padding-right: 0.75rem;

  @media (min-width: ${breakpoints.s}) {
    gap: 1rem;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

// Fixed footer (buttons and help link)
export const fixedFooter = css`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 1rem;
`;

// Header section
export const headerSection = css`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-shrink: 0;
`;

export const iconContainer = css`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

export const iconWrapper = (theme: BirdiTheme) => css`
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background-color: ${theme.ocean};
  svg {
    color: ${theme.mono0};
    width: 1.5em;
    height: 1.5em;
  }
`;

export const title = (theme: BirdiTheme) => css`
  font-size: 1.25rem;
  font-weight: bold;
  color: ${theme.midnight};
  margin: 0;
`;

export const subtitle = (theme: BirdiTheme) => css`
  font-size: 1rem;
  color: ${theme.mono60};
  margin: 1em 0 0 0;
`;

// Form styles
export const formContainer = css`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  @media (min-width: ${breakpoints.s}) {
    gap: 0.5rem;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }
`;

export const formField = css`
  display: flex;
  flex-direction: column;
  // gap: 0.25rem;
  flex-shrink: 0;
`;

export const label = (theme: BirdiTheme) => css`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.midnight};
  display: flex;
  align-items: center;
  gap: 0.25rem;

  .required {
    color: #ef4444;
  }
`;

export const inputWrapper = css`
  position: relative;
  display: flex;
  align-items: center;
`;

export const inputIcon = css`
  position: absolute;
  left: 0.875rem;
  width: 1rem;
  height: 1rem;
  color: #9ca3af;
  pointer-events: none;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
    // fill: currentColor;
  }
`;

export const input = (theme: BirdiTheme) => css`
  width: 100%;
  min-height: 3rem;
  padding: 0.5rem 0.875rem 0.5rem 2.5rem;
  border: 1px solid ${theme.mono30};
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  color: ${theme.mono70};
  background-color: ${theme.mono0};
  transition:
    border-color 0.2s,
    box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: ${theme.deepOcean};
    box-shadow: 0 0 0 3px ${theme.deepOcean}20;
  }

  &::placeholder {
    color: ${theme.mono50};
  }

  &:disabled {
    background-color: ${theme.mono20};
    color: ${theme.mono50};
    cursor: not-allowed;
  }
`;

export const checkboxContainer = css`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

export const checkbox = (theme: BirdiTheme) => css`
  width: 1.25rem;
  height: 1.25rem;
  margin-top: 0.125rem;
  cursor: pointer;
  accent-color: ${theme.deepOcean};
`;

export const checkboxLabel = (theme: BirdiTheme) => css`
  font-size: 0.875rem;
  color: ${theme.mono70};
  line-height: 1.5;
  cursor: pointer;

  a {
    color: ${theme.deepOcean};
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// Button styles
export const buttonContainer = css`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0;
  flex-shrink: 0;
`;

export const primaryButton = (theme: BirdiTheme) => css`
  width: 100%;
  padding: 0.75rem 1.25rem;
  background-color: ${theme.ocean};
  color: ${theme.mono0};
  border: none;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  box-shadow: none;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 2px 2px 4px ${theme.mono40};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background-color: ${theme.mono30};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const secondaryButton = (theme: BirdiTheme) => css`
  width: 100%;
  padding: 0.75rem 1.25rem;
  background-color: ${theme.mono0};
  color: ${theme.mono70};
  border: 1px solid ${theme.mono30};
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition:
    border-color 0.2s,
    background-color 0.2s;

  &:hover {
    border-color: ${theme.mono40};
    background-color: ${theme.mono10};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background-color: ${theme.mono20};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

// Help link
export const helpLink = (theme: BirdiTheme) => css`
  text-align: center;
  margin-top: 0.25rem;
  font-size: 0.8125rem;
  color: ${theme.mono60};
  flex-shrink: 0;

  a {
    color: ${theme.deepOcean};
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// Error message
export const errorMessage = css`
  font-size: 0.875rem;
  color: #ef4444;
  margin-top: 0.25rem;
`;
